// components/RestaurantMenuManagement/DesktopMenuManagement.tsx
import React from "react";
import { Edit, Trash2 } from "lucide-react";

interface DesktopMenuManagementProps {
    filteredMenu: any[];
    loading: boolean;
    error: string | null;
    openEditModal: (item: any) => void;
    handleDelete: (id: string) => void;
    handleOutOfStock: (item: any) => void;
    loadingIds: Set<string>;
    outOfStockIcon: string;
}

const DesktopMenuManagement: React.FC<DesktopMenuManagementProps> = ({
    filteredMenu,
    loading,
    error,
    openEditModal,
    handleDelete,
    handleOutOfStock,
    loadingIds,
    outOfStockIcon,
}) => {
    return (
        <div className="relative overflow-x-auto rounded-xl shadow-md bg-white/70 dark:bg-gray-900/60 backdrop-blur-md border border-gray-200/40 dark:border-gray-700/50 hidden md:block">
            <table className="min-w-full text-xs md:text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gradient-to-r from-gray-100/80 to-gray-50/50 dark:from-gray-800/80 dark:to-gray-900/50 backdrop-blur">
                    <tr>
                        {["Image", "Name", "Description", "Category", "Subcat", "Price", "Disc. Price", "Flags", "Actions"].map((header) => (
                            <th
                                key={header}
                                className="px-3 py-2 font-medium text-gray-700 dark:text-gray-200 text-left whitespace-nowrap"
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {loading ? (
                        <tr>
                            <td colSpan={9} className="py-4 text-center text-gray-500 dark:text-gray-400">
                                Loading menu...
                            </td>
                        </tr>
                    ) : filteredMenu.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="py-4 text-center text-gray-500 dark:text-gray-400">
                                No menu items found.
                            </td>
                        </tr>
                    ) : (
                        filteredMenu.map((item) => (
                            <tr
                                key={item.id}
                                className="hover:bg-gray-50/80 dark:hover:bg-gray-800/60 transition-all duration-150"
                            >
                                {/* Image */}
                                <td className="px-2 py-1.5">
                                    <img
                                        src={item.imageUrl || "https://placehold.co/64x64"}
                                        alt={item.name}
                                        className="w-14 h-14 rounded-xl object-cover shadow-sm transition-transform duration-300 hover:scale-105"
                                    />
                                </td>

                                {/* Name */}
                                <td className="px-2 py-1.5 font-semibold truncate text-gray-900 dark:text-gray-100">
                                    {item.name}
                                </td>

                                {/* Description */}
                                <td className="px-2 py-1.5 truncate max-w-[10rem] text-gray-600 dark:text-gray-300">
                                    {item.description}
                                </td>

                                {/* Category */}
                                <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                                    {item.category}
                                </td>

                                {/* Subcategory */}
                                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">
                                    {item.subcategory || "-"}
                                </td>

                                {/* Price + Discount */}
                                <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">₹{item.price}</td>
                                <td className="px-2 py-1.5 text-green-500 dark:text-green-400">
                                    {item.discountPrice ? `₹${item.discountPrice}` : 'NA'}
                                </td>

                                {/* Flags */}
                                <td className="px-2 py-1.5">
                                    {item.isAvailable ? (
                                        <span className="bg-green-100/70 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 rounded-full text-[11px]">
                                            Available
                                        </span>
                                    ) : (
                                        <span className="bg-red-100/70 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 rounded-full text-[11px]">
                                            Out of stock
                                        </span>
                                    )}
                                </td>

                                {/* Actions */}
                                <td className="px-3 py-2">
                                    <div className="flex gap-1 items-center justify-center">
                                        {/* ✏️ Edit */}
                                        <button
                                            onClick={() => openEditModal(item)}
                                            className="text-blue-500 hover:text-blue-600 flex items-center justify-center w-8 h-8 rounded-full transition-transform transform hover:scale-110 hover:shadow-md hover:shadow-blue-200/50 dark:hover:shadow-blue-800/30"
                                            title="Edit Item"
                                        >
                                            <Edit size={20} />
                                        </button>

                                        {/* 🗑️ Delete */}
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="text-red-500 hover:text-red-600 flex items-center justify-center w-8 h-8 rounded-full transition-transform transform hover:scale-110 hover:shadow-md hover:shadow-red-200/50 dark:hover:shadow-red-800/30"
                                            title="Delete Item"
                                        >
                                            <Trash2 size={20} />
                                        </button>

                                        {/* ⚙️ Stock Toggle */}
                                        <button
                                            onClick={() => handleOutOfStock(item)}
                                            disabled={loadingIds.has(item.id)}
                                            title={item.isAvailable ? 'Mark as Out of Stock' : 'Mark as Available'}
                                            className="flex items-center justify-center w-8 h-8 rounded-full transition-transform transform hover:scale-110 hover:shadow-md hover:shadow-gray-200/50 dark:hover:shadow-gray-800/30"
                                        >
                                            {loadingIds.has(item.id) ? (
                                                <span className="block w-5 h-5 border-[2px] border-t-transparent border-gray-400 rounded-full animate-spin"></span>
                                            ) : (
                                                <img
                                                    src={outOfStockIcon}
                                                    alt="Stock"
                                                    className={`w-5 h-5 object-contain transition-all duration-300 ${item.isAvailable ? 'opacity-100' : 'opacity-60 grayscale'
                                                        }`}
                                                    style={{ minWidth: '20px', minHeight: '20px' }}
                                                />
                                            )}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            {error && <div className="text-center text-red-500 mt-3 text-sm">{error}</div>}
        </div>
    );
};

export default DesktopMenuManagement;
